import { Ticket,  FileSpreadsheet,  Activity } from "lucide-react";
import {  useEffect, useCallback } from "react"
import { Link, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  LayoutDashboard, Wallet, CreditCard, Shield,
  Building2, Workflow, ChevronLeft, ChevronRight,
  Fingerprint, Home, Receipt, X
, TrendingDown, Bell, Sparkles, ShieldAlert, UserCheck} from "lucide-react"
import WalletCard from "./WalletCard"

// Grupos de navegación por contexto
const buildGroups = (user, isSuperadmin) => {
  const groups = [
    {
      label: "Principal",
      items: [
        { to: "/dashboard",     icon: LayoutDashboard, label: "Dashboard" },
        { to: "/notifications", icon: Bell,            label: "Notificaciones" },
        { to: "/wallet",        icon: Wallet,          label: "Billetera" },
        { to: "/recarga",         icon: CreditCard,      label: "Recargar" },
        { to: "/panel/planes",  icon: Sparkles,        label: "Planes" },
      ],
    },
    {
      label: "Verificación",
      items: [
        { to: "/panel/flows",   icon: Workflow,        label: "Flujos Hosted" },
        { to: "/security",      icon: Shield,          label: "Seguridad" },
      ],
    },
    {
      label: "Empresa",
      items: [
        { to: "/mi-empresa",    icon: Building2,       label: user?.companyId ? "Mi Empresa" : "Crear Empresa" },
      ],
    },
  ]

  if (isSuperadmin) {
    groups.push({
      label: "Admin",
      items: [
        { to: "/admin",              icon: Home,    label: "Panel Admin" },
        { to: "/admin/convert",      icon: Activity, label: "Divisas (USD/PEN)" },
        { to: "/admin/pad",          icon: ShieldAlert, label: "PAD · Anti-spoofing" },
        { to: "/admin/manual-review", icon: UserCheck, label: "Manual Review" },
        { to: "/admin/facturacion", icon: FileSpreadsheet, label: "Facturación" },
        { to: "/admin/coupons", icon: Ticket, label: "Cupones" },
        { to: "/admin/topups",       icon: Receipt, label: "TopUps" },
        { to: "/admin/hosted-flows", icon: Workflow,label: "Flujos Hosted" },
        { to: "/admin/funnel",       icon: TrendingDown, label: "Funnel" },
        { to: "/admin/security-events", icon: Shield,  label: "Seguridad" },
      ],
    })
  }

  return groups
}

function SidebarItem({ item, active, collapsed, onNavigate }) {
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={[
        "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
        active
          ? "bg-gradient-brand text-white shadow-card"
          : "text-surface-600 hover:bg-brand-50 hover:text-brand-700",
        collapsed ? "justify-center" : "",
      ].join(" ")}
      title={collapsed ? item.label : undefined}
    >
      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? "text-white" : "group-hover:text-brand-600"}`} strokeWidth={2.2} />
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.15 }}
            className="whitespace-nowrap overflow-hidden"
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
      {active && !collapsed && (
        <motion.div
          layoutId="active-pill"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-white/60"
        />
      )}
    </Link>
  )
}

function SidebarContent({ user, collapsed, onToggle, onCloseMobile }) {
  const loc = useLocation()
  const isSuperadmin = ["admin", "superadmin"].includes(user?.role)
  const groups = buildGroups(user, isSuperadmin)

  return (
    <>
      {/* Logo + toggle */}
      <div className={`flex items-center h-16 px-4 border-b border-surface-200/60 ${collapsed ? "justify-center" : "justify-between"}`}>
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-brand flex items-center justify-center shadow-card flex-shrink-0 group-hover:shadow-glow transition-shadow duration-300">
            <Fingerprint className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="font-display font-bold text-base text-surface-900 whitespace-nowrap overflow-hidden"
              >
                iDaaS<span className="text-brand ml-1">Perú</span>
              </motion.span>
            )}
          </AnimatePresence>
        </Link>

        {/* Toggle desktop */}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center text-surface-400 hover:bg-surface-100 hover:text-surface-700 transition-colors"
            title="Colapsar sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        {/* Cerrar mobile — F10.08c: onClick + onPointerDown + console.log */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onCloseMobile?.()
          }}
          className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center text-surface-500 hover:bg-surface-100"
          aria-label="Cerrar menú"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Expandir (solo cuando colapsado) */}
      {collapsed && (
        <button
          onClick={onToggle}
          className="hidden lg:flex mx-auto mt-2 w-8 h-8 rounded-lg items-center justify-center text-surface-400 hover:bg-surface-100 hover:text-surface-700 transition-colors"
          title="Expandir sidebar"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Navegación */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {groups.map((group) => (
          <div key={group.label}>
            <AnimatePresence>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-surface-400"
                >
                  {group.label}
                </motion.div>
              )}
            </AnimatePresence>
            <div className="space-y-1">
              {group.items.map((item) => (
                <SidebarItem
                  key={item.to}
                  item={item}
                  active={loc.pathname === item.to || (item.to !== "/" && loc.pathname.startsWith(item.to))}
                  collapsed={collapsed}
                  onNavigate={onCloseMobile}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* F8.92: WalletCard (billetera en soles + estado bono) */}
      <WalletCard collapsed={collapsed} />

      {/* Footer: versión */}
      <div className="px-3 py-3 border-t border-surface-200/60">
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[10px] text-surface-400 text-center"
            >
              v1.0 · idaas.lat
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

export default function Sidebar({ user, collapsed, onToggle, mobileOpen, onCloseMobile }) {
  // F10.08b: listener global de fallback — si el usuario toca fuera del drawer
  // y el onPointerDown del backdrop no se disparó (por backdrop-filter en móvil),
  // cerramos igual. Detecta touch/pointer fuera del rect del drawer.
  const handleGlobalPointerDown = useCallback((e) => {
    if (!mobileOpen) return
    const drawer = document.getElementById("idaas-mobile-drawer")
    if (!drawer) return
    if (drawer.contains(e.target)) return  // click dentro del drawer → no cerrar
    onCloseMobile?.()
  }, [mobileOpen, onCloseMobile])

  useEffect(() => {
    if (!mobileOpen) return
    document.addEventListener("pointerdown", handleGlobalPointerDown, true)
    return () => document.removeEventListener("pointerdown", handleGlobalPointerDown, true)
  }, [mobileOpen, handleGlobalPointerDown])

  return (
    <>
      {/* Desktop sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 248 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-white/95 backdrop-blur-xl border-r border-surface-200/60 z-40"
      >
        <SidebarContent user={user} collapsed={collapsed} onToggle={onToggle} onCloseMobile={onCloseMobile} />
      </motion.aside>

      {/* F10.08c: NUCLEAR — sin AnimatePresence, CSS puro. pointer-events:none cuando cerrado */}
      {/* Backdrop: siempre en DOM, opacity + pointer-events toggle */}
      <div
        onClick={onCloseMobile}
        className="lg:hidden fixed inset-0 z-40"
        style={{
          backgroundColor: "rgba(15, 23, 42, 0.4)",
          opacity: mobileOpen ? 1 : 0,
          pointerEvents: mobileOpen ? "auto" : "none",
          transition: "opacity 200ms ease",
        }}
        aria-hidden="true"
      />

      {/* Drawer: siempre en DOM, transform toggle. visibility:hidden cuando cerrado */}
      <aside
        id="idaas-mobile-drawer"
        className="lg:hidden fixed left-0 top-0 h-screen w-[280px] bg-white border-r border-surface-200 flex flex-col z-50"
        style={{
          transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 250ms ease",
          visibility: mobileOpen ? "visible" : "hidden",
          pointerEvents: mobileOpen ? "auto" : "none",
        }}
      >
        <SidebarContent user={user} collapsed={collapsed} onToggle={onToggle} onCloseMobile={onCloseMobile} />
      </aside>
    </>
  )
}
